import { Spin, message } from "antd";
import { Plus, FileText, CheckSquare } from "lucide-react";
import { useState, useMemo, useCallback } from "react";
import { useGetInventories, INVENTORY_KEYS } from "~/services/inventory";
import { inventoryService } from "~/services/inventory/inventory.services";
import { useInventoryStore } from "../inventory.store";
import { ConsolidationActions } from "./consolidation-actions";
import { FilterInventories } from "./filter-inventories";
import { MetricInventories } from "./metric-inventories";
import { TableInventory } from "./table-inventory";
import { ImportInventoryModal } from "../import-inventory-modal";
import { InventoryStatusCode, DocumentType } from "~/utils/constants";
import QuickPkgButton from '../Quick-pkg/QuickPkgButton';
import { PendingImportFiles } from "../pending-import-files";
import { useGetFiles } from "~/services/file/file.queries";
import { ExportModal, ExportType } from "~/components/client/inventory/export-modal";
import { exportToCSV, exportToPDF, exportPhotoList } from "~/components/client/inventory/export-utils";
import { InventoryBulkUpdateButton } from "~/components/admin/bookings/detail-booking/tabs/inventory-bulk-update-button";
import { TInventory } from "~/types";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";


export const ListInventory = () => {
  const { filters, setFilters, openInventoryModal } = useInventoryStore();
  const [viewMode, setViewMode] = useState<"all" | "storage" | "pending-imports">("all");
  const queryClient = useQueryClient();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedItemsMap, setSelectedItemsMap] = useState<Map<string, TInventory>>(new Map());
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Make sure to destructure refetch from the hook
  const { data: inventories, isLoading, refetch } = useGetInventories({
    ...filters,
    ...(viewMode === "storage"
      ? { status: InventoryStatusCode.AVAILABLE_ON_HAND }
      : {}),
  });

  // With preserveSelectedRowKeys, Ant Design gives us ALL keys across pages
  const handleSelectionChange = useCallback((allKeys: string[], allRows: TInventory[]) => {
    setSelectedRowKeys(allKeys);
    setSelectedItemsMap((prevMap) => {
      const newMap = new Map<string, TInventory>();
      // Keep items from previous selections that are still selected
      for (const key of allKeys) {
        const fromRows = allRows.find((r) => r?.id === key);
        if (fromRows) {
          newMap.set(key, fromRows);
        } else if (prevMap.has(key)) {
          // Item from another page — preserve from map
          newMap.set(key, prevMap.get(key)!);
        }
      }
      return newMap;
    });
  }, []);

  const [isSelectingAll, setIsSelectingAll] = useState(false);

  const handleSelectAll = useCallback(async () => {
    const totalItems = inventories?.meta.totalItems ?? 0;
    if (totalItems === 0) return;

    // If everything is already selected, deselect all
    if (selectedRowKeys.length === totalItems) {
      setSelectedRowKeys([]);
      setSelectedItemsMap(new Map());
      return;
    }

    setIsSelectingAll(true);
    try {
      const queryParams = {
        ...filters,
        ...(viewMode === "storage"
          ? { status: InventoryStatusCode.AVAILABLE_ON_HAND }
          : {}),
        page: 1,
        pageSize: totalItems,
      };
      const allData = await inventoryService.getInventories(queryParams);
      const allItems = allData.items || [];
      const allKeys = allItems.map((item) => item.id);
      const newMap = new Map<string, TInventory>();
      allItems.forEach((item) => newMap.set(item.id, item));
      setSelectedRowKeys(allKeys);
      setSelectedItemsMap(newMap);
    } catch (err) {
      console.error("Select all error:", err);
      message.error("Failed to select all items.");
    } finally {
      setIsSelectingAll(false);
    }
  }, [inventories?.meta.totalItems, selectedRowKeys.length, filters, viewMode]);

  const inventoriesToExport = useMemo(() => {
    if (selectedRowKeys.length > 0) {
      return Array.from(selectedItemsMap.values());
    }
    return inventories?.items || [];
  }, [selectedRowKeys, selectedItemsMap, inventories?.items]);

  const handleExport = async (columns: string[], type: ExportType, action: "view" | "download") => {
    try {
      switch (type) {
        case "csv":
          exportToCSV(inventoriesToExport, columns, action);
          break;
        case "pdf":
          exportToPDF(inventoriesToExport, columns, action);
          break;
        case "photo-list":
          await exportPhotoList(inventoriesToExport, columns, action);
          break;
      }
      setIsExportModalOpen(false);
      message.success(`Export ${action === "view" ? "opened" : "downloaded"} successfully`);
    } catch (err) {
      console.error("Export error:", err);
      message.error("Failed to export. Please try again.");
    }
  };

  // Get pending import files count
  const { data: filesData } = useGetFiles(
    { type: DocumentType.INVENTORY, page: 1, pageSize: 50 },
    true
  );

  const pendingImportCount = useMemo(() => {
    if (!filesData?.items) return 0;
    
    // Load completed file IDs from localStorage
    let completedFileIds = new Set<string>();
    try {
      const stored = localStorage.getItem("completed_inventory_imports");
      if (stored) {
        const data = JSON.parse(stored);
        const thirtyDaysAgo = dayjs().subtract(30, 'days');
        Object.entries(data).forEach(([id, timestamp]: [string, any]) => {
          if (dayjs(timestamp).isAfter(thirtyDaysAgo)) {
            completedFileIds.add(id);
          }
        });
      }
    } catch (error) {
      console.error("Error loading completed imports:", error);
    }

    // Filter files to only show recent ones (last 30 days) and only files with valid size
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const allFiles = filesData.items.filter((file) => {
      if (!file.createdAt) return false;
      const fileDate = new Date(file.createdAt);
      const isRecent = fileDate >= thirtyDaysAgo;
      
      const hasValidSize = file.size && 
                           file.size.trim() !== "" && 
                           !isNaN(parseInt(file.size)) && 
                           parseInt(file.size) > 0;
      
      return isRecent && hasValidSize;
    });

    // Count pending files (not completed)
    return allFiles.filter((file) => !completedFileIds.has(file.id)).length;
  }, [filesData?.items]);

  return (
    <div className="bg-gray-50">
      <div className="max-w-full mx-auto">
        {/* Header and Title */}
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-2xl font-semibold">Inventory Management</h1>
          <div className="flex space-x-3">
            <ImportInventoryModal />
          </div>
        </div>

        {/* View Toggle */}
        <div className="mb-4 bg-white rounded-lg border p-2 shadow-sm">
          <div className="flex space-x-2">
            <button
              className={`px-4 py-2 rounded-lg ${
                viewMode === "all"
                  ? "bg-blue-50 text-blue-700"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setViewMode("all")}
            >
              All Inventory
            </button>
            <button
              className={`px-4 py-2 rounded-lg ${
                viewMode === "storage"
                  ? "bg-blue-50 text-blue-700"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setViewMode("storage")}
            >
              Storage
            </button>
            <button
              className={`px-4 py-2 rounded-lg ${
                viewMode === "pending-imports"
                  ? "bg-blue-50 text-blue-700"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setViewMode("pending-imports")}
            >
              Pending Imports
              {pendingImportCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                  {pendingImportCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Metrics Overview - Only show for inventory views */}
        {viewMode !== "pending-imports" && <MetricInventories />}

        {/* Content based on view mode */}
        {viewMode === "pending-imports" ? (
          <PendingImportFiles />
        ) : (
          <>
            {/* Search and Filter Bar */}
            <FilterInventories
              totalItems={inventories?.meta.totalItems ?? 0}
              currentItemsCount={inventories?.items?.length ?? 0}
              actions={
                <>
                  <button
                    onClick={handleSelectAll}
                    disabled={isSelectingAll}
                    className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-2 text-sm font-medium disabled:opacity-50"
                  >
                    <CheckSquare size={14} />
                    <span>
                      {isSelectingAll
                        ? "Selecting..."
                        : selectedRowKeys.length === (inventories?.meta.totalItems ?? 0)
                          ? "Deselect All"
                          : "Select All"}
                    </span>
                  </button>
                  <button
                    onClick={() => openInventoryModal()}
                    className="px-3 py-2 bg-blue-600 text-white border border-blue-600 rounded-lg hover:bg-blue-700 flex items-center space-x-2 text-sm font-medium"
                  >
                    <Plus size={14} />
                    <span>Create Inventory</span>
                  </button>
                  <QuickPkgButton
                    onSuccess={() => refetch()}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2 text-sm font-medium"
                  />
                  <button
                    onClick={() => {
                      if (inventoriesToExport.length === 0) {
                        message.warning("No items to export.");
                        return;
                      }
                      setIsExportModalOpen(true);
                    }}
                    className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-2 text-sm font-medium"
                  >
                    <FileText size={14} />
                    <span>Export{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ""}</span>
                  </button>
                </>
              }
            />

            {/* Selection Info Bar — only show when items are selected */}
            {selectedRowKeys.length > 0 && (
              <div className="flex items-center justify-between mb-4 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-blue-800">
                    {selectedRowKeys.length} of {inventories?.meta.totalItems ?? 0} items selected
                  </span>
                  <button
                    onClick={() => {
                      setSelectedRowKeys([]);
                      setSelectedItemsMap(new Map());
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear selection
                  </button>
                </div>
                <InventoryBulkUpdateButton
                  selectedInventories={Array.from(selectedItemsMap.values())}
                  onUpdateComplete={() => {
                    setSelectedRowKeys([]);
                    setSelectedItemsMap(new Map());
                    queryClient.invalidateQueries({ queryKey: INVENTORY_KEYS.GET_INVENTORIES({}) });
                  }}
                />
              </div>
            )}

            {/* Inventory List */}
            <Spin spinning={isLoading} size="large">
              <TableInventory
                inventories={inventories?.items}
                selectedRowKeys={selectedRowKeys}
                onSelectionChange={handleSelectionChange}
                pagination={{
                  current: filters.page,
                  pageSize: filters.pageSize,
                  total: inventories?.meta.totalItems ?? 0,
                  onChange: (page, pageSize) => {
                    setFilters({ ...filters, page, pageSize });
                  },
                  hideOnSinglePage: false,
                }}
              />
            </Spin>
          </>
        )}

        <ConsolidationActions />

        {/* Export Modal */}
        <ExportModal
          open={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          exportType="csv"
          inventories={inventoriesToExport}
          onExport={handleExport}
        />
      </div>
    </div>
  );
};